import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj) return obj;
  
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj) return obj;
  
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
}

// Get all classrooms for authenticated teacher
export async function getClassrooms() {
  try {
    // Verify authentication
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('classrooms')?.select('*')?.eq('teacher_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return data?.map(toCamelCase) || [];
  } catch (error) {
    console.error('Error fetching classrooms:', error);
    throw error;
  }
}

// Get single classroom by ID
export async function getClassroomById(classroomId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('classrooms')?.select('*')?.eq('id', classroomId)?.eq('teacher_id', user?.id)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return data ? toCamelCase(data) : null;
  } catch (error) {
    console.error('Error fetching classroom:', error);
    throw error;
  }
}

// Create new classroom
export async function createClassroom(classroomData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const snakeData = toSnakeCase(classroomData);
    snakeData.teacher_id = user?.id;

    const { data, error } = await supabase?.from('classrooms')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error creating classroom:', error);
    throw error;
  }
}

// Update classroom
export async function updateClassroom(classroomId, classroomData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const snakeData = toSnakeCase(classroomData);
    delete snakeData?.id;
    delete snakeData?.teacher_id;
    delete snakeData?.created_at;

    const { data, error } = await supabase?.from('classrooms')?.update(snakeData)?.eq('id', classroomId)?.eq('teacher_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating classroom:', error);
    throw error;
  }
}

// Delete classroom
export async function deleteClassroom(classroomId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase?.from('classrooms')?.delete()?.eq('id', classroomId)?.eq('teacher_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return true;
  } catch (error) {
    console.error('Error deleting classroom:', error);
    throw error;
  }
}

// Promote all students in a classroom to the next grade level
export async function promoteAllStudents(classroomId, nextGradeLevel) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Update classroom grade_level
    const { error: classroomError } = await supabase
      ?.from('classrooms')
      ?.update({ grade_level: nextGradeLevel })
      ?.eq('id', classroomId)
      ?.eq('teacher_id', user?.id);

    if (classroomError) {
      throw new Error(classroomError?.message);
    }

    return true;
  } catch (error) {
    console.error('Error promoting students:', error);
    throw error;
  }
}